/**
 * 운영 프로세스 워크플로(업무절차 디자인) — 순수 로직.
 *
 * 새 워크플로 뷰(OperationProcessWorkflow)가 쓰는 진행 계산과 불변 mutator
 * 모음. React/저장소 의존이 없어 단독 테스트 가능하다.
 *
 * 진행 단위(unit) 규칙 — 업무절차 디자인 그대로:
 *   하위 단계(children)가 있는 행은 하위 단계 각각이 1 단위,
 *   없으면 행 자신이 1 단위. 진행률/현재·다음 단계는 unit 기준.
 *
 * 데이터 보존: 모든 mutator 는 spread 기반이라 인터페이스에 없는 과거 필드
 * (preparation 등)도 그대로 유지한 채 저장된다.
 */
import type {
  OperationChecklistItem,
  OperationProcessChildRow,
  OperationProcessRow,
} from '../../services/OperationProcessService';
import { CATEGORIES, makeChild, makeRow } from './operationProcessShared';

export type WorkflowStepStatus = 'done' | 'current' | 'next' | 'todo';

/** 진행 단위 참조 — childId 가 없으면 행 자신. */
export interface WorkflowUnitRef {
  rowId: string;
  childId?: string;
}

export interface WorkflowStepView {
  row: OperationProcessRow;
  index: number;
  status: WorkflowStepStatus;
  done: boolean;
}

export interface WorkflowView {
  steps: WorkflowStepView[];
  doneCount: number;
  totalCount: number;
  percent: number;
  current: WorkflowUnitRef | null;
  next: WorkflowUnitRef | null;
  currentLabel: string;
  nextLabel: string;
  /** 다음 단계 카드 보조 문구 — 체크리스트 잔여/완료 또는 준비사항 메모. */
  nextPrep: string;
}

export const childrenOf = (row: OperationProcessRow): OperationProcessChildRow[] =>
  Array.isArray(row.children) ? row.children : [];

export const checklistOf = (node: {
  checklist?: OperationChecklistItem[];
}): OperationChecklistItem[] => (Array.isArray(node.checklist) ? node.checklist : []);

export function categoryLabel(value: string): string {
  return CATEGORIES.find((c) => c.value === value)?.label ?? '기타';
}

/** 행 완료 여부 — 하위 단계가 있으면 전부 완료일 때만 완료. */
export function isStepDone(row: OperationProcessRow): boolean {
  const children = childrenOf(row);
  return children.length > 0 ? children.every((c) => !!c.checked) : !!row.checked;
}

interface Unit extends WorkflowUnitRef {
  checked: boolean;
}

function listUnits(rows: OperationProcessRow[]): Unit[] {
  return rows.flatMap((r) => {
    const children = childrenOf(r);
    if (children.length === 0) return [{ rowId: r.id, checked: !!r.checked }];
    return children.map((c) => ({ rowId: r.id, childId: c.id, checked: !!c.checked }));
  });
}

/** unit 이 가리키는 행/자식 노드. 없으면 null. */
export function findUnitNode(
  rows: OperationProcessRow[],
  ref: WorkflowUnitRef,
): OperationProcessRow | OperationProcessChildRow | null {
  const row = rows.find((r) => r.id === ref.rowId);
  if (!row) return null;
  if (!ref.childId) return row;
  return childrenOf(row).find((c) => c.id === ref.childId) ?? null;
}

/** "1. 장비 설치" / "2-1. 맵 데이터 생성" 형식의 단계 라벨. */
export function unitLabel(rows: OperationProcessRow[], ref: WorkflowUnitRef): string {
  const ri = rows.findIndex((r) => r.id === ref.rowId);
  if (ri < 0) return '';
  const row = rows[ri];
  const rowName = (row.name || '').trim() || `${categoryLabel(row.category)} #${ri + 1}`;
  if (!ref.childId) return `${ri + 1}. ${rowName}`;
  const children = childrenOf(row);
  const ci = children.findIndex((c) => c.id === ref.childId);
  if (ci < 0) return `${ri + 1}. ${rowName}`;
  const childName = (children[ci].name || '').trim() || '하위 단계';
  return `${ri + 1}-${ci + 1}. ${childName}`;
}

function nextPrepText(rows: OperationProcessRow[], next: WorkflowUnitRef | null): string {
  if (!next) return '';
  const node = findUnitNode(rows, next);
  if (!node) return '';
  const list = checklistOf(node);
  const left = list.filter((c) => !c.done).length;
  if (left > 0) return `준비물 ${left}개 확인 필요`;
  if (list.length > 0) return '준비물 확인 완료';
  // 체크리스트가 없으면 행 단위의 준비사항 메모(preparation)를 대신 보여준다.
  if (!next.childId) return ((node as OperationProcessRow).preparation || '').trim();
  return '';
}

export function buildWorkflowView(rows: OperationProcessRow[]): WorkflowView {
  const units = listUnits(rows);
  const doneCount = units.filter((u) => u.checked).length;
  const curIdx = units.findIndex((u) => !u.checked);
  const current = curIdx >= 0 ? units[curIdx] : null;
  const next = curIdx >= 0 && curIdx + 1 < units.length ? units[curIdx + 1] : null;
  const percent = units.length > 0 ? Math.round((doneCount / units.length) * 100) : 0;

  const steps: WorkflowStepView[] = rows.map((row, index) => {
    const done = isStepDone(row);
    const status: WorkflowStepStatus = done
      ? 'done'
      : current?.rowId === row.id
        ? 'current'
        : next?.rowId === row.id
          ? 'next'
          : 'todo';
    return { row, index, status, done };
  });

  return {
    steps,
    doneCount,
    totalCount: units.length,
    percent,
    current,
    next,
    currentLabel:
      units.length === 0
        ? '등록된 단계 없음'
        : current
          ? unitLabel(rows, current)
          : '모든 단계 완료',
    nextLabel: next ? unitLabel(rows, next) : current ? '마지막 단계입니다' : '—',
    nextPrep: nextPrepText(rows, next),
  };
}

// ─── 불변 mutator ────────────────────────────────────────────────────────────

export function makeChecklistItem(text: string): OperationChecklistItem {
  return {
    id: `ck-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    text,
    done: false,
  };
}

export type WorkflowRowPatch = Partial<
  Pick<
    OperationProcessRow,
    'name' | 'worker' | 'date' | 'remark' | 'content' | 'preparation' | 'category' | 'docLinks'
  >
>;
export type WorkflowChildPatch = Partial<
  Pick<OperationProcessChildRow, 'name' | 'worker' | 'date' | 'remark' | 'content'>
>;

export function patchRow(
  rows: OperationProcessRow[],
  rowId: string,
  patch: WorkflowRowPatch,
): OperationProcessRow[] {
  return rows.map((r) => (r.id === rowId ? { ...r, ...patch } : r));
}

export function patchChild(
  rows: OperationProcessRow[],
  rowId: string,
  childId: string,
  patch: WorkflowChildPatch,
): OperationProcessRow[] {
  return rows.map((r) =>
    r.id === rowId
      ? { ...r, children: childrenOf(r).map((c) => (c.id === childId ? { ...c, ...patch } : c)) }
      : r,
  );
}

/** 체크리스트를 가진 노드(행/자식)에 fn 을 적용한다. */
function mapUnit<T>(
  rows: OperationProcessRow[],
  ref: WorkflowUnitRef,
  fn: (node: T) => T,
): OperationProcessRow[] {
  return rows.map((r) => {
    if (r.id !== ref.rowId) return r;
    if (!ref.childId) return fn(r as unknown as T) as unknown as OperationProcessRow;
    return {
      ...r,
      children: childrenOf(r).map((c) =>
        c.id === ref.childId ? (fn(c as unknown as T) as unknown as OperationProcessChildRow) : c,
      ),
    };
  });
}

type HasChecklist = { checklist?: OperationChecklistItem[] };

export function addChecklistItem(
  rows: OperationProcessRow[],
  ref: WorkflowUnitRef,
  text: string,
): OperationProcessRow[] {
  const trimmed = text.trim();
  if (!trimmed) return rows;
  return mapUnit<HasChecklist>(rows, ref, (node) => ({
    ...node,
    checklist: [...checklistOf(node), makeChecklistItem(trimmed)],
  }));
}

export function toggleChecklistItem(
  rows: OperationProcessRow[],
  ref: WorkflowUnitRef,
  itemId: string,
): OperationProcessRow[] {
  return mapUnit<HasChecklist>(rows, ref, (node) => ({
    ...node,
    checklist: checklistOf(node).map((c) => (c.id === itemId ? { ...c, done: !c.done } : c)),
  }));
}

export function removeChecklistItem(
  rows: OperationProcessRow[],
  ref: WorkflowUnitRef,
  itemId: string,
): OperationProcessRow[] {
  return mapUnit<HasChecklist>(rows, ref, (node) => ({
    ...node,
    checklist: checklistOf(node).filter((c) => c.id !== itemId),
  }));
}

/**
 * 단위 완료/해지.
 *  - 자식 단위: 자식 checked 변경 + 부모 checked 를 "자식 전부 완료" 로 동기화
 *    (기존 Table/Viewer·진행률 위젯이 부모 checked 를 읽으므로).
 *  - 행 단위: 행 checked 변경 (+ 하위가 있으면 같은 상태로 캐스케이드 — 기존
 *    Viewer 의 부모 토글 규칙과 동일).
 *  - 완료 처리 시 해당 노드의 체크리스트도 모두 완료로 (업무절차 디자인 규칙).
 */
export function setUnitDone(
  rows: OperationProcessRow[],
  ref: WorkflowUnitRef,
  done: boolean,
): OperationProcessRow[] {
  const completeChecklist = (node: HasChecklist): OperationChecklistItem[] | undefined => {
    if (!done || !node.checklist) return node.checklist;
    return node.checklist.map((c) => ({ ...c, done: true }));
  };
  return rows.map((r) => {
    if (r.id !== ref.rowId) return r;
    if (ref.childId) {
      const children = childrenOf(r).map((c) =>
        c.id === ref.childId ? { ...c, checked: done, checklist: completeChecklist(c) } : c,
      );
      return { ...r, children, checked: children.every((c) => !!c.checked) };
    }
    return {
      ...r,
      checked: done,
      checklist: completeChecklist(r),
      children: childrenOf(r).map((c) => ({ ...c, checked: done })),
    };
  });
}

export function addStep(rows: OperationProcessRow[]): {
  rows: OperationProcessRow[];
  id: string;
} {
  const row = { ...makeRow('etc'), name: '새 단계' };
  return { rows: [...rows, row], id: row.id };
}

export function addSubStep(
  rows: OperationProcessRow[],
  rowId: string,
): { rows: OperationProcessRow[]; id: string } {
  const child = { ...makeChild(), name: '새 하위 단계' };
  return {
    rows: rows.map((r) =>
      // 새 하위는 미완료이므로 부모 checked 도 미완료로 동기화.
      r.id === rowId ? { ...r, children: [...childrenOf(r), child], checked: false } : r,
    ),
    id: child.id,
  };
}

export function removeStep(rows: OperationProcessRow[], rowId: string): OperationProcessRow[] {
  return rows.filter((r) => r.id !== rowId);
}

export function removeSubStep(
  rows: OperationProcessRow[],
  rowId: string,
  childId: string,
): OperationProcessRow[] {
  return rows.map((r) => {
    if (r.id !== rowId) return r;
    const children = childrenOf(r).filter((c) => c.id !== childId);
    // 남은 하위가 있으면 부모 checked 재계산, 전부 삭제되면 기존 값 유지.
    return {
      ...r,
      children,
      ...(children.length > 0 ? { checked: children.every((c) => !!c.checked) } : {}),
    };
  });
}

function moveInArray<T>(list: T[], from: number, to: number): T[] {
  if (from < 0 || to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

export function moveStep(
  rows: OperationProcessRow[],
  rowId: string,
  dir: -1 | 1,
): OperationProcessRow[] {
  const idx = rows.findIndex((r) => r.id === rowId);
  return moveInArray(rows, idx, idx + dir);
}

export function moveSubStep(
  rows: OperationProcessRow[],
  rowId: string,
  childId: string,
  dir: -1 | 1,
): OperationProcessRow[] {
  return rows.map((r) => {
    if (r.id !== rowId) return r;
    const children = childrenOf(r);
    const idx = children.findIndex((c) => c.id === childId);
    return { ...r, children: moveInArray(children, idx, idx + dir) };
  });
}

// ─── 업무절차 디자인 토큰 ────────────────────────────────────────────────────
// 업무절차.html 의 팔레트/서체를 단일 출처로. 라이트가 원본 디자인이고,
// 다크는 같은 위계(강조 카드 반전, 초록 악센트 유지)를 어두운 중성색 위에
// 옮긴 팔레트. 컴포넌트는 workflowTokens(mode) 로 앱 테마와 동기화한다.
export const WF = {
  font: "'Gothic A1', 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif",
  bg: '#faf9f6',
  ink: '#201f1c',
  paper: '#ffffff',
  acc: 'oklch(0.55 0.18 155)',
  accDeep: 'oklch(0.45 0.15 155)',
  accBright: 'oklch(0.8 0.15 155)',
  accPale: 'oklch(0.95 0.03 155)',
  muted: '#8a8577',
  faint: '#b3ad9d',
  ghost: '#c9c3b2',
  body: '#33312b',
  subInk: '#6f6a5c',
  btnInk: '#4a463c',
  line: '#e6e3da',
  lineSoft: '#e0dcd0',
  lineSub: '#e9e6db',
  lineFaint: '#f0ede3',
  dash: '#d8d3c4',
  track: '#eae7dd',
  chipBg: '#f2efe6',
  nextBg: '#fdf3e3',
  nextFg: '#96690f',
  danger: '#a03e2e',
  subCurBg: '#fbfff9',
  /** 강조(반전) 표면 — 라이트에선 다크 카드, 다크에선 라이트 카드. */
  inverseBg: '#201f1c',
  inverseFg: '#faf9f6',
  /** 반전 표면 위의 악센트 라벨 색. */
  inverseLabel: 'oklch(0.8 0.15 155)',
} as const;

export type WfTokens = Record<keyof typeof WF, string>;

/** 다크 모드 팔레트 — 웜 그레이 스케일 반전 + 악센트 명도 상향. */
export const WF_DARK: WfTokens = {
  font: WF.font,
  bg: '#191815',
  ink: '#f4f2ea',
  paper: '#201f1c',
  acc: 'oklch(0.62 0.17 155)',
  accDeep: 'oklch(0.75 0.14 155)',
  accBright: 'oklch(0.8 0.15 155)',
  accPale: 'oklch(0.32 0.06 155)',
  muted: '#a49d8c',
  faint: '#787264',
  ghost: '#5f5a4e',
  body: '#dcd8cc',
  subInk: '#b5af9f',
  btnInk: '#cdc8b9',
  line: '#38362e',
  lineSoft: '#403d33',
  lineSub: '#343228',
  lineFaint: '#2c2a24',
  dash: '#4c4839',
  track: '#302e28',
  chipBg: '#2d2b25',
  nextBg: '#3b2f16',
  nextFg: '#e2b45f',
  danger: '#e2735f',
  subCurBg: '#1e2a20',
  inverseBg: '#f4f2ea',
  inverseFg: '#191815',
  inverseLabel: 'oklch(0.45 0.15 155)',
};

/** 앱 테마 모드(palette.mode)에 맞는 워크플로 토큰. */
export function workflowTokens(mode?: string): WfTokens {
  return mode === 'dark' ? WF_DARK : WF;
}
